import ENDPOINTS from 'shared/api/endpoints';
import RequestClient from 'shared/api/http-client';
import UrlModifier from 'shared/utils/url';
import { getDomainForApi } from 'shared/utils/common';

import type { Event, Schedule } from './types';

/** Default number of upcoming events to load for event selector and list views. */
export const UPCOMING_EVENTS_LIST_SIZE = 10;

export async function fetchSchedules(): Promise<Schedule[]> {
  const domain = getDomainForApi();
  const params = domain ? { domain } : undefined;
  const res = await RequestClient.get(ENDPOINTS.SCHEDULES_LISTS, params ? { params } : undefined);
  const data = res.data;
  return Array.isArray(data) ? (data as Schedule[]) : [];
}

export async function fetchUpcomingEvents(params: {
  schedule_id: number | string;
  num_events: number;
}): Promise<Event[]> {
  const { schedule_id, num_events } = params;

  const url = UrlModifier.bindToUrl(ENDPOINTS.EVENTS_NEXT_LISTS, { num_events });
  if (!url || typeof url !== 'string') throw new Error('Invalid API endpoint URL');
  const res = await RequestClient.get(url, {
    params: { schedule_id },
  });

  const data = res.data;
  const list = Array.isArray(data) ? data : Object.values(data || {});
  return list as Event[];
}

export async function fetchScheduleWeeks(params: { schedule_id: number | string }): Promise<number[]> {
  const url = UrlModifier.bindToUrl(ENDPOINTS.SCHEDULE_WEEKS, { schedule_id: params.schedule_id });
  if (!url || typeof url !== 'string') throw new Error('Invalid API endpoint URL');
  const res = await RequestClient.get(url);
  const data = res.data;
  const list = Array.isArray(data) ? data : Object.values(data || {});
  return list.map((n: any) => Number(n)).filter((n: number) => Number.isFinite(n));
}

export async function fetchScheduleWeekEvents(params: {
  schedule_id: number | string;
  week_id: number;
}): Promise<Event[]> {
  const url = UrlModifier.bindToUrl(ENDPOINTS.SCHEDULE_WEEK_EVENTS, { schedule_id: params.schedule_id, weekId: params.week_id });
  if (!url || typeof url !== 'string') throw new Error('Invalid API endpoint URL');
  const res = await RequestClient.get(url);
  const data = res.data;
  const list = Array.isArray(data) ? data : Object.values(data || {});
  return list as Event[];
}

export type Football3dWeek = {
  week_id: number;
  week_number: number;
  status: 'SCHEDULED' | 'OPEN' | 'CLOSED';
  events: Event[];
};

export type Football3dWeeksAndEvents = {
  weeks: Football3dWeek[];
};

export async function fetchFootball3dWeeksAndEvents(params: {
  schedule_id: number | string;
}): Promise<Football3dWeeksAndEvents> {
  const url = UrlModifier.bindToUrl(ENDPOINTS.SCHEDULE_FOOTBALL3D_WEEKS_AND_EVENTS, { schedule_id: params.schedule_id });
  if (!url || typeof url !== 'string') throw new Error('Invalid API endpoint URL');
  const res = await RequestClient.get(url);
  const data = res.data as Football3dWeeksAndEvents | undefined;
  if (!data || typeof data !== 'object') {
    return { weeks: [] };
  }
  return {
    weeks: Array.isArray(data.weeks) ? data.weeks : [],
  };
}

export type ScheduleStanding = {
  position: number;
  team_id: number;
  team_code: string;
  team_name: string;
  points: number;
  wins: number;
  draws: number;
  losses: number;
};

export async function fetchScheduleStandings(params: {
  schedule_id: number | string;
  week_id?: number;
}): Promise<ScheduleStanding[]> {
  const url = UrlModifier.bindToUrl(ENDPOINTS.SCHEDULE_STANDINGS, { schedule_id: params.schedule_id });
  if (!url || typeof url !== 'string') throw new Error('Invalid API endpoint URL');
  const res = await RequestClient.get(url, { params: params.week_id != null ? { week_id: params.week_id } : {} });
  const data = res.data;
  return Array.isArray(data) ? (data as ScheduleStanding[]) : [];
}

export type ScheduleResultItem = {
  event_id: number;
  event_timestamp: number;
  week_id: number | null;
  week_number: number | null;
  sport_id: number;
  home_team_code: string;
  away_team_code: string;
  home_score: number;
  away_score: number;
  home_score_1ht?: number;
  away_score_1ht?: number;
  featured?: boolean;
  [key: string]: unknown;
};

export async function fetchScheduleResults(params: {
  schedule_id: number | string;
}): Promise<ScheduleResultItem[]> {
  const url = UrlModifier.bindToUrl(ENDPOINTS.SCHEDULE_RESULTS, { schedule_id: params.schedule_id });
  if (!url || typeof url !== 'string') throw new Error('Invalid API endpoint URL');
  const res = await RequestClient.get(url);
  const data = res.data;
  return Array.isArray(data) ? (data as ScheduleResultItem[]) : [];
}

/** Paginated: GET /web/schedules/:schedule_id/results?limit=10&offset=0 → { results, has_more } */
export type ScheduleResultsPage = {
  results: ScheduleResultItem[];
  has_more: boolean;
};

export async function fetchScheduleResultsPage(params: {
  schedule_id: number | string;
  limit?: number;
  offset?: number;
}): Promise<ScheduleResultsPage> {
  const { schedule_id, limit = 10, offset = 0 } = params;
  const url = UrlModifier.bindToUrl(ENDPOINTS.SCHEDULE_RESULTS, { schedule_id: String(schedule_id) });
  if (!url || typeof url !== 'string') throw new Error('Invalid API endpoint URL');
  const res = await RequestClient.get(url, {
    params: { limit, offset },
  });
  const data = res?.data;
  if (data && typeof data === 'object' && 'results' in data) {
    return {
      results: Array.isArray(data.results) ? (data.results as ScheduleResultItem[]) : [],
      has_more: Boolean(data.has_more),
    };
  }
  return { results: [], has_more: false };
}

/** Response shape from GET /web/schedules/:scheduleId/events/upcoming (racing/baseball: flat events only; ongoing from GET /live). */
export type ScheduleUpcomingRacingBaseball = {
  events: Event[];
  /** @deprecated Backend no longer sends ongoing; use GET /web/schedules/:scheduleId/live */
  ongoing?: Record<string, unknown> | null;
};

/** Response shape from GET /web/schedules/:scheduleId/events/upcoming (Football3D: nested weeks with events). */
export type ScheduleUpcomingFootball3d = {
  weeks: Football3dWeek[];
};

export type ScheduleUpcomingResponse = ScheduleUpcomingRacingBaseball | ScheduleUpcomingFootball3d;

export async function fetchScheduleUpcoming(params: {
  schedule_id: number | string;
  num_events?: number;
}): Promise<ScheduleUpcomingResponse> {
  const { schedule_id, num_events = UPCOMING_EVENTS_LIST_SIZE } = params;
  const url =
    UrlModifier.bindToUrl(ENDPOINTS.SCHEDULE_EVENTS_UPCOMING, { schedule_id: String(schedule_id) });
  if (!url || typeof url !== 'string') throw new Error('Invalid API endpoint URL');
  const res = await RequestClient.get(url, {
    params: { num_events },
  });
  const data = res?.data;
  if (data === undefined) throw new Error('API response missing data');
  return data as ScheduleUpcomingResponse;
}

/** Event results with optional VOD (for mini TV). From GET /web/schedules/:scheduleId/events/:eventId/results */
export type ScheduleEventResultsResponse = Record<string, unknown> & {
  event_id?: number;
  video_id?: string;
  video_id_sequence?: string[];
  vod?: { hls_url?: string; urls?: string[]; exp?: number; avh?: unknown } | null;
  cancelled?: boolean;
};

export async function fetchScheduleEventResults(params: {
  schedule_id: number | string;
  event_id: number | string;
}): Promise<ScheduleEventResultsResponse> {
  const { schedule_id, event_id } = params;
  const url =
    UrlModifier.bindToUrl(ENDPOINTS.SCHEDULE_EVENT_RESULTS, {
      schedule_id: String(schedule_id),
      event_id: String(event_id),
    });
  if (!url || typeof url !== 'string') throw new Error('Invalid API endpoint URL');
  const res = await RequestClient.get(url);
  const data = res?.data;
  if (data === undefined) throw new Error('API response missing data');
  return data as ScheduleEventResultsResponse;
}

